import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/layout/SiteHeader';
import { SiteFooter } from '../components/layout/SiteFooter';
import { useAuth } from '../auth/AuthContext';

// ─── Sidebar nav ────────────────────────────────────────────────────────────

const NAV = [
  { group: 'Getting Started' },
  { id: 'quickstart',         label: 'Quick Start'           },
  { id: 'authentication',     label: 'Authentication'        },
  { id: 'rate-limits',        label: 'Rate Limits'           },
  { group: 'API Reference'   },
  { id: 'create-simulation',  label: 'POST /simulations',    method: 'POST' },
  { id: 'get-simulation',     label: 'GET /simulations/:id', method: 'GET'  },
  { group: 'Reference'       },
  { id: 'errors',             label: 'Errors'                },
];

// ─── Parameter schema data ──────────────────────────────────────────────────

const CREATE_PARAMS = [
  {
    field: 'measurementUnits', type: 'object', req: 'required',
    desc: 'Unit system applied to the entire request. All four sub-fields are required.',
    children: [
      { field: 'distance',    type: 'string', req: 'required', enm: '"meter"',     desc: 'Distance unit. Only "meter" is currently supported.' },
      { field: 'weight',      type: 'string', req: 'required', enm: '"kilogram"',  desc: 'Weight unit. Only "kilogram" is currently supported.' },
      { field: 'currency',    type: 'string', req: 'required', enm: '"usd"',       desc: 'Currency for container costs. Only "usd" is currently supported.' },
      { field: 'utilisation', type: 'string', req: 'required', enm: '"percent"',   desc: 'Utilisation unit. Only "percent" is currently supported.' },
    ],
  },
  {
    field: 'handlingType', type: 'string', req: 'required',
    enm: '"hand_stuffed" | "palletized"',
    desc: 'Packing mode. hand_stuffed places items directly into containers. palletized first loads items onto pallets, then pallets into containers. Determines whether the pallets field is required or forbidden.',
  },
  {
    field: 'items', type: 'object', req: 'required', constraint: 'min 1 key',
    desc: 'Map of item type IDs (any string) to item objects. Keys are arbitrary labels for each cargo type (e.g. "Carton-A", "Drum-B").',
    children: [
      { field: '[id].length',   type: 'number',  req: 'required', constraint: '> 0', desc: 'Item length in meters.' },
      { field: '[id].width',    type: 'number',  req: 'required', constraint: '> 0', desc: 'Item width in meters.' },
      { field: '[id].height',   type: 'number',  req: 'required', constraint: '> 0', desc: 'Item height in meters.' },
      { field: '[id].weight',   type: 'number',  req: 'required', constraint: '> 0', desc: 'Weight per unit in kilograms.' },
      { field: '[id].quantity', type: 'integer', req: 'required', constraint: '> 0', desc: 'Total units of this item type to pack.' },
      { field: '[id].sameStackingOnly',  type: 'boolean', req: 'required', desc: 'When true, only the same item type may be stacked on top of this item. Useful for fragile or top-heavy cargo.' },
      { field: '[id].maxStackingLevel',  type: 'integer', req: 'required', constraint: '> 0', desc: 'Maximum height of a single vertical stack (number of items stacked on top of each other).' },
      {
        field: '[id].possibleOrientations', type: 'array', req: 'required',
        desc: 'Allowed rotation configurations. Each element defines one orientation the engine may apply. Include multiple elements to allow rotation.',
        children: [
          { field: '[].length', type: 'string', req: 'required', enm: '"length" | "width"', desc: 'Which item dimension is placed along the container length axis.' },
          { field: '[].width',  type: 'string', req: 'required', enm: '"length" | "width"', desc: 'Which item dimension is placed along the container width axis.' },
          { field: '[].height', type: 'string', req: 'required', enm: '"height"',           desc: 'Always "height". Items cannot be placed on their side.' },
        ],
      },
      { field: '[id].itemsPerPalletLayer', type: 'integer', req: 'optional', constraint: '> 0 · nullable', desc: 'Palletized mode only. Caps items per pallet layer. The engine validates this against the computed pallet footprint at request time.' },
    ],
  },
  {
    field: 'containers', type: 'object', req: 'required', constraint: 'min 1 key',
    desc: 'Map of container type IDs to container specifications. Define any dimensions — standard ISO types or fully custom sizes.',
    children: [
      { field: '[id].length', type: 'number', req: 'required', constraint: '> 0', desc: 'Internal usable length in meters.' },
      { field: '[id].width',  type: 'number', req: 'required', constraint: '> 0', desc: 'Internal usable width in meters.' },
      { field: '[id].height', type: 'number', req: 'required', constraint: '> 0', desc: 'Internal usable height in meters.' },
      {
        field: '[id].weight', type: 'object', req: 'required',
        desc: 'Weight capacity configuration.',
        children: [
          { field: 'value',          type: 'number', req: 'required', constraint: '> 0',   desc: 'Maximum payload capacity in kilograms (max gross weight minus tare weight).' },
          { field: 'maxUtilisation', type: 'number', req: 'required', constraint: '0–100', desc: 'Maximum weight utilisation percentage. Set below 100 to preserve a safety buffer (e.g. 95).' },
        ],
      },
      { field: '[id].maxVolumeUtilisation', type: 'number', req: 'required', constraint: '0–100', desc: 'Maximum volume utilisation percentage. Accounts for irregular stacking, pallets, and structural constraints inside the container.' },
      { field: '[id].cost', type: 'number', req: 'required', constraint: '> 0', desc: 'Cost per container unit in the selected currency. The engine minimises total cost across all container type combinations.' },
    ],
  },
  {
    field: 'pallets', type: 'object', req: 'conditional', constraint: 'min 1 key',
    reqNote: 'Required when handlingType is "palletized". Must be omitted for "hand_stuffed".',
    desc: 'Map of pallet type IDs to pallet objects.',
    children: [
      { field: '[id].length',           type: 'number',  req: 'required', constraint: '> 0', desc: 'Pallet length in meters.' },
      { field: '[id].width',            type: 'number',  req: 'required', constraint: '> 0', desc: 'Pallet width in meters.' },
      { field: '[id].height',           type: 'number',  req: 'required', constraint: '> 0', desc: 'Pallet height (tare) in meters. Added to item height for container clearance checks.' },
      { field: '[id].weight',           type: 'number',  req: 'required', constraint: '> 0', desc: 'Pallet tare weight in kilograms. Added to cargo weight for container capacity checks.' },
      { field: '[id].maxWeightAllowed', type: 'number',  req: 'required', constraint: '> 0', desc: 'Maximum load weight (excluding pallet tare) that can be placed on this pallet.' },
      { field: '[id].sameItemOnly',     type: 'boolean', req: 'required', desc: 'When true, the engine will not mix different item types on a single pallet.' },
      { field: '[id].isDoubleStackingAllowed', type: 'boolean', req: 'optional', dflt: 'false', desc: 'Allow loaded pallets to be stacked two high inside the container.' },
    ],
  },
];

const RESPONSE_PARAMS = [
  { field: 'success', type: 'boolean', desc: 'true on success, false on error.' },
  {
    field: 'data', type: 'object', desc: 'Present on success.',
    children: [
      { field: 'id', type: 'string', desc: 'UUID for this simulation. Append to https://feuteuristix.com/console?id= for the shareable 3D viewer link.' },
      {
        field: 'entities', type: 'object', desc: 'Processed echo of input data.',
        children: [
          { field: 'items',            type: 'object', desc: 'Processed items — includes a color assigned for 3D rendering.' },
          { field: 'containers',       type: 'object', desc: 'Processed containers — includes computed volume object.' },
          { field: 'pallets',          type: 'object', desc: 'Palletized mode only. Processed pallet definitions.' },
          { field: 'handlingType',     type: 'string', desc: 'Echo of request handlingType.' },
          { field: 'measurementUnits', type: 'object', desc: 'Echo of request measurementUnits.' },
        ],
      },
      {
        field: 'strategies', type: 'object', desc: 'Results wrapper — always access via strategies.default.',
        children: [
          {
            field: 'default', type: 'object', desc: 'The single results key. Always literally "default".',
            children: [
              {
                field: 'analysis', type: 'object', desc: 'Cost summary.',
                children: [
                  { field: 'cost.totalCost',         type: 'number',  desc: 'Total cost across all containers booked.' },
                  { field: 'cost.containerQuantity', type: 'integer', desc: 'Total number of containers booked.' },
                  { field: 'cost.containers',        type: 'array',   desc: 'Per-type cost breakdown. Each element: { container, quantity, totalCost }.' },
                ],
              },
              {
                field: 'packedContainers', type: 'array', desc: 'One element per container booking in the optimal plan.',
                children: [
                  { field: '[].id',          type: 'string',  desc: 'Container type ID (matches a key from request containers).' },
                  { field: '[].packingType', type: 'string',  desc: '"FCL" (full container load) or "SPL" (partial / split load).' },
                  { field: '[].quantity',    type: 'integer', desc: 'Number of identical containers in this booking.' },
                  { field: '[].totalCost',   type: 'number',  desc: 'Cost for this booking (quantity × unit cost).' },
                  {
                    field: '[].stats', type: 'object', desc: 'Utilisation statistics per container.',
                    children: [
                      { field: 'itemCount',                  type: 'integer', desc: 'Total items packed per container.' },
                      { field: 'weight.value',               type: 'number',  desc: 'Total cargo weight per container in kg.' },
                      { field: 'weight.utilisation',         type: 'number',  desc: 'Weight utilisation as % of container capacity.' },
                      { field: 'volume.value',               type: 'number',  desc: 'Total cargo volume per container in m³.' },
                      { field: 'volume.utilisation',         type: 'number',  desc: 'Volume utilisation as % of container capacity.' },
                      { field: 'volume.realisedUtilisation', type: 'number',  desc: 'Effective volume utilisation based on actual stack heights — more accurate than nominal.' },
                      { field: 'itemDetails',                type: 'array',   desc: 'Per item type: { id, quantity } packed per container.' },
                      { field: 'palletCount',                type: 'integer', desc: 'Palletized mode only. Total pallets per container.' },
                      { field: 'palletDetails',              type: 'array',   desc: 'Palletized mode only. Per pallet type: { id, quantity }.' },
                    ],
                  },
                  {
                    field: '[].stacks', type: 'array', desc: 'Full packing plan. Each element is one vertical stack — a column of items at a single [x,y] position.',
                    children: [
                      { field: '[].[].item',        type: 'string',    desc: 'Item type ID for this stack.' },
                      { field: '[].[].quantity',    type: 'integer',   desc: 'Items in this vertical stack (stacking height).' },
                      { field: '[].[].orientation', type: 'object',    desc: 'Rotation applied: length and width are "length"|"width"; height is always "height".' },
                      { field: '[].[].coordinates', type: 'number[3]', desc: '[x, y, z] position in meters from the container origin (front-left-bottom corner).' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

const GET_PATH_PARAMS = [
  { field: ':id', type: 'string', req: 'required', desc: 'UUID returned in the id field of the POST /simulations response.' },
];

// ─── Code examples ───────────────────────────────────────────────────────────

const HAND_STUFFED_REQUEST = {
  measurementUnits: { distance: 'meter', weight: 'kilogram', currency: 'usd', utilisation: 'percent' },
  handlingType: 'hand_stuffed',
  items: {
    'Carton-A': {
      length: 0.5, width: 0.4, height: 0.3,
      weight: 12, quantity: 200,
      sameStackingOnly: false, maxStackingLevel: 4,
      possibleOrientations: [
        { length: 'length', width: 'width', height: 'height' },
        { length: 'width',  width: 'length', height: 'height' },
      ],
    },
    'Drum-B': {
      length: 0.6, width: 0.6, height: 0.9,
      weight: 80, quantity: 20,
      sameStackingOnly: true, maxStackingLevel: 1,
      possibleOrientations: [{ length: 'length', width: 'width', height: 'height' }],
    },
  },
  containers: {
    '20ft Standard': { length: 5.9, width: 2.35, height: 2.39, weight: { value: 21800, maxUtilisation: 95 }, maxVolumeUtilisation: 90, cost: 1800 },
    '40ft HC':       { length: 12.03, width: 2.35, height: 2.69, weight: { value: 26460, maxUtilisation: 95 }, maxVolumeUtilisation: 90, cost: 2900 },
  },
};

const PALLETIZED_REQUEST = {
  measurementUnits: { distance: 'meter', weight: 'kilogram', currency: 'usd', utilisation: 'percent' },
  handlingType: 'palletized',
  items: {
    'Box-S': {
      length: 0.4, width: 0.3, height: 0.2,
      weight: 5, quantity: 500,
      sameStackingOnly: false, maxStackingLevel: 5, itemsPerPalletLayer: 6,
      possibleOrientations: [{ length: 'length', width: 'width', height: 'height' }],
    },
  },
  containers: {
    '40ft HC': { length: 12.03, width: 2.35, height: 2.69, weight: { value: 26460, maxUtilisation: 95 }, maxVolumeUtilisation: 90, cost: 2900 },
  },
  pallets: {
    EURO: { length: 1.2, width: 0.8, height: 0.15, weight: 25, maxWeightAllowed: 1500, sameItemOnly: false, isDoubleStackingAllowed: false },
  },
};

const CREATE_RESPONSE = {
  success: true,
  data: {
    id: 'a3f72b1c-4d5e-4f6a-b7c8-d9e0f1a2b3c4',
    entities: {
      items: {
        'Carton-A': { length: 0.5, width: 0.4, height: 0.3, weight: 12, quantity: 200, color: '#1E90FF' },
        'Drum-B':   { length: 0.6, width: 0.6, height: 0.9, weight: 80, quantity: 20,  color: '#FF6347' },
      },
      containers: {
        '20ft Standard': { length: 5.9,  width: 2.35, height: 2.39, volume: { value: 33.14, maxUtilisation: 90 }, weight: { value: 21800, maxUtilisation: 95 }, cost: 1800 },
        '40ft HC':       { length: 12.03, width: 2.35, height: 2.69, volume: { value: 76.06, maxUtilisation: 90 }, weight: { value: 26460, maxUtilisation: 95 }, cost: 2900 },
      },
      handlingType: 'hand_stuffed',
      measurementUnits: { distance: 'meter', weight: 'kilogram', currency: 'usd', utilisation: 'percent' },
    },
    strategies: {
      default: {
        analysis: {
          cost: {
            totalCost: 7600,
            containerQuantity: 3,
            containers: [
              { container: '40ft HC',       quantity: 2, totalCost: 5800 },
              { container: '20ft Standard', quantity: 1, totalCost: 1800 },
            ],
          },
        },
        packedContainers: [
          {
            id: '40ft HC', packingType: 'FCL', quantity: 2, totalCost: 5800,
            stats: {
              itemCount: 168, weight: { value: 1952, utilisation: 7.38 },
              volume: { value: 9.72, utilisation: 12.78, realisedUtilisation: 100 },
              itemDetails: [{ id: 'Carton-A', quantity: 168 }],
            },
            stacks: [
              [{ item: 'Carton-A', quantity: 4, orientation: { length: 'length', width: 'width', height: 'height' }, coordinates: [0, 0, 0] }],
              [{ item: 'Carton-A', quantity: 4, orientation: { length: 'length', width: 'width', height: 'height' }, coordinates: [0.5, 0, 0] }],
            ],
          },
          {
            id: '20ft Standard', packingType: 'SPL', quantity: 1, totalCost: 1800,
            stats: {
              itemCount: 52, weight: { value: 2024, utilisation: 9.28 },
              volume: { value: 3.85, utilisation: 11.62, realisedUtilisation: 100 },
              itemDetails: [
                { id: 'Carton-A', quantity: 32 },
                { id: 'Drum-B',   quantity: 20 },
              ],
            },
            stacks: [
              [{ item: 'Carton-A', quantity: 4, orientation: { length: 'length', width: 'width', height: 'height' }, coordinates: [0, 0, 0] }],
              [{ item: 'Drum-B',   quantity: 1, orientation: { length: 'length', width: 'width', height: 'height' }, coordinates: [0, 0.9, 0] }],
            ],
          },
        ],
      },
    },
  },
};

const GET_RESPONSE = {
  success: true,
  data: {
    id: 'a3f72b1c-4d5e-4f6a-b7c8-d9e0f1a2b3c4',
    entities: { '...': 'same as POST response entities' },
    strategies: { '...': 'same as POST response strategies' },
  },
};

const ERROR_RESPONSE_400 = { success: false, message: '"measurementUnits.distance" is required' };
const ERROR_RESPONSE_404 = { success: false, message: 'No such resource found, please check the id' };
const ERROR_RESPONSE_500 = { success: false, message: 'Internal server error' };

// ─── Helper components ───────────────────────────────────────────────────────

function MethodBadge({ method }) {
  const styles = {
    POST: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    GET:  'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  };
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 font-mono text-xs font-bold tracking-wider ${styles[method]}`}>
      {method}
    </span>
  );
}

function TypeBadge({ type }) {
  const styles = {
    object: 'bg-violet-500/15 text-violet-300',
    array:  'bg-amber-500/15 text-amber-300',
    string: 'bg-sky-500/15 text-sky-300',
    number: 'bg-blue-500/15 text-blue-300',
    integer:'bg-blue-500/15 text-blue-300',
    boolean:'bg-rose-500/15 text-rose-300',
    'string[]':'bg-amber-500/15 text-amber-300',
    'number[3]':'bg-blue-500/15 text-blue-300',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${styles[type] || 'bg-slate-700 text-slate-300'}`}>
      {type}
    </span>
  );
}

function ReqBadge({ req }) {
  if (req === 'required')    return <span className="rounded px-1.5 py-0.5 text-[10px] font-bold bg-red-500/10 text-red-400">required</span>;
  if (req === 'conditional') return <span className="rounded px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/10 text-amber-400">conditional</span>;
  if (req === 'optional')    return <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-700 text-slate-400">optional</span>;
  return null;
}

function highlightJson(str) {
  return str
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (m) => {
        let s = 'color:#60a5fa';
        if (/^"/.test(m)) s = /:$/.test(m) ? 'color:#e2e8f0' : 'color:#fcd34d';
        else if (/true|false/.test(m)) s = 'color:#c084fc';
        else if (/null/.test(m)) s = 'color:#64748b';
        return `<span style="${s}">${m}</span>`;
      }
    );
}

// Plain code block (for short snippets like HTTP headers)
function CodeBlock({ code, language = 'http' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-slate-900">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{language}</span>
        <button onClick={copy} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition">
          <span className="material-symbols-outlined text-[13px]">{copied ? 'check' : 'content_copy'}</span>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto px-5 py-4 text-sm leading-relaxed text-slate-300">{code}</pre>
    </div>
  );
}

// Collapsible JSON block — collapses to ~14 lines with gradient fade
function JsonBlock({ obj, label }) {
  const [expanded, setExpanded] = useState(false);
  const [copied,   setCopied]   = useState(false);

  const raw   = JSON.stringify(obj, null, 2);
  const html  = highlightJson(raw);
  const lines = raw.split('\n').length;
  const PREVIEW = 14;
  const isLong  = lines > PREVIEW;

  const copy = () => { navigator.clipboard.writeText(raw); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-slate-900">
      {/* toolbar */}
      <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-slate-700" />
          <span className="h-2 w-2 rounded-full bg-slate-700" />
          <span className="h-2 w-2 rounded-full bg-slate-700" />
          {label && <span className="ml-2 text-xs text-slate-500">{label}</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">json</span>
          <button onClick={copy} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition">
            <span className="material-symbols-outlined text-[13px]">{copied ? 'check' : 'content_copy'}</span>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {/* collapsed preview */}
      {!expanded && isLong && (
        <div className="relative" style={{ maxHeight: `${PREVIEW * 1.625 * 13}px`, overflow: 'hidden' }}>
          <pre className="px-5 py-4 text-sm leading-relaxed overflow-x-auto"
               dangerouslySetInnerHTML={{ __html: html }} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-slate-900 to-transparent" />
        </div>
      )}

      {/* full content */}
      {(expanded || !isLong) && (
        <pre className="overflow-x-auto px-5 py-4 text-sm leading-relaxed"
             dangerouslySetInnerHTML={{ __html: html }} />
      )}

      {/* expand / collapse bar */}
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-center gap-1.5 border-t border-white/10 bg-white/[0.03] py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-200 transition"
        >
          <span className="material-symbols-outlined text-[14px]">{expanded ? 'unfold_less' : 'unfold_more'}</span>
          {expanded ? 'Collapse' : `Show all ${lines} lines`}
        </button>
      )}
    </div>
  );
}

const DEPTH_COLORS = ['', 'border-l border-primary/20 ml-3', 'border-l border-blue-500/20 ml-6', 'border-l border-cyan-500/20 ml-9'];

function ParamRow({ p, depth, hasChildren, expanded, onToggle }) {
  const depthCls = DEPTH_COLORS[Math.min(depth, 3)];
  return (
    <div
      className={`${depthCls} ${depth > 0 ? 'bg-white/[0.015]' : ''} ${onToggle ? 'cursor-pointer hover:bg-white/[0.03]' : ''} border-b border-white/[0.04] last:border-0 transition-colors`}
      onClick={onToggle}
    >
      <div className="flex items-start gap-4 px-4 py-3">
        {/* Field name + badges */}
        <div className="w-44 shrink-0 min-w-0">
          <div className="flex items-center gap-1">
            {hasChildren ? (
              <span className={`material-symbols-outlined text-[12px] text-slate-500 transition-transform duration-200 shrink-0 ${expanded ? 'rotate-90' : ''}`}>
                chevron_right
              </span>
            ) : <span className="w-3 shrink-0" />}
            <code className="font-mono text-sm text-white break-all leading-snug">{p.field}</code>
          </div>
          <div className="mt-1.5 ml-3 flex flex-wrap items-center gap-1">
            <TypeBadge type={p.type} />
            {p.req && <ReqBadge req={p.req} />}
          </div>
        </div>

        {/* Description + meta */}
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {p.constraint && <code className="rounded bg-slate-800/80 px-1.5 py-0.5 font-mono text-xs text-slate-300">{p.constraint}</code>}
            {p.enm        && <code className="rounded bg-amber-950/50 px-1.5 py-0.5 font-mono text-xs text-amber-300">{p.enm}</code>}
            {p.dflt !== undefined && <code className="rounded bg-slate-800/60 px-1.5 py-0.5 font-mono text-xs text-slate-400">default: {String(p.dflt)}</code>}
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
          {p.reqNote && <p className="mt-1 text-xs text-amber-400/80">{p.reqNote}</p>}
          {hasChildren && !expanded && (
            <p className="mt-1 text-xs text-primary/60">
              {p.children.length} {p.children.length === 1 ? 'child field' : 'child fields'} — click to expand
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ParamTable({ params, title }) {
  const [expanded, setExpanded] = useState(new Set());

  const toggle = (path) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(path) ? next.delete(path) : next.add(path);
    return next;
  });

  const renderRows = (rows, depth = 0, parentPath = '') =>
    rows.map((p) => {
      const path        = `${parentPath}/${p.field}`;
      const hasChildren = Array.isArray(p.children) && p.children.length > 0;
      const isExpanded  = expanded.has(path);
      return (
        <React.Fragment key={path}>
          <ParamRow p={p} depth={depth} hasChildren={hasChildren} expanded={isExpanded}
            onToggle={hasChildren ? () => toggle(path) : undefined} />
          {hasChildren && isExpanded && renderRows(p.children, depth + 1, path)}
        </React.Fragment>
      );
    });

  return (
    <div>
      {title && <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">{title}</p>}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {renderRows(params)}
      </div>
    </div>
  );
}

function SectionCard({ children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] overflow-hidden">
      {children}
    </div>
  );
}

function SectionTitle({ title }) {
  return <h2 className="text-2xl font-black text-white">{title}</h2>;
}

function EndpointBanner({ method, path, summary }) {
  return (
    <div className="border-b border-white/10 bg-white/[0.03] px-7 py-5">
      <div className="flex items-center gap-3 flex-wrap mb-1.5">
        <MethodBadge method={method} />
        <code className="font-mono text-base font-bold text-white">{path}</code>
      </div>
      {summary && <p className="text-sm text-slate-400 leading-relaxed max-w-2xl">{summary}</p>}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-xs font-semibold text-slate-200">{value}</span>
    </div>
  );
}

function SubLabel({ children }) {
  return <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">{children}</p>;
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function DevelopersPage() {
  const { isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState('quickstart');
  const [reqTab,        setReqTab]        = useState('hand_stuffed');
  const mainRef = useRef(null);

  useEffect(() => {
    const SECTIONS = NAV.filter((n) => n.id).map((n) => n.id);
    const onScroll = () => {
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i]);
        if (el && el.getBoundingClientRect().top <= 130) {
          setActiveSection(SECTIONS[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Container Load Planning API — REST API for Logistics Platforms | Feuteuristix</title>
        <meta name="description" content="One API call returns optimal container selection, full 3D packing plan, cost breakdown, and coordinates. Embed container intelligence in your TMS or portal in under 30 minutes." />
        <meta property="og:title" content="Container Load Planning API — REST API for Logistics Platforms | Feuteuristix" />
        <meta property="og:description" content="One API call returns optimal container selection, full 3D packing plan, cost breakdown, and coordinates. Embed container intelligence in your TMS or portal in under 30 minutes." />
        <meta property="og:url" content="https://feuteuristix.com/developers" />
        <meta name="twitter:title" content="Container Load Planning API — REST API for Logistics Platforms | Feuteuristix" />
        <meta name="twitter:description" content="One API call returns optimal container selection, full 3D packing plan, cost breakdown, and coordinates. Embed container intelligence in your TMS or portal in under 30 minutes." />
        <link rel="canonical" href="https://feuteuristix.com/developers" />
      </Helmet>
      <SiteHeader />

      <main className="flex-1 pt-24">

        {/* Hero */}
        <section className="border-b border-white/10 py-14 bg-gradient-to-b from-background-dark to-slate-950/60">
          <div className="mx-auto max-w-6xl px-6 md:px-10">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              API Reference
            </span>
            <h1 className="mt-4 text-4xl font-black leading-tight md:text-5xl">
              Add intelligent container<br className="hidden md:block" /> planning to your platform.
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              One REST call returns the cheapest container mix, a full packing plan with 3D coordinates, cost analysis, and a shareable viewer link. Integrate in a day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-semibold text-slate-300">API operational</span>
              </div>
              <Pill label="Base URL" value="https://api.feuteuristix.com" />
              <Pill label="Version" value="v1" />
              <Pill label="Auth" value="x-api-key header" />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={isAuthenticated ? '/console?tab=api-keys' : '/login?mode=signup&redirect=/console?tab=api-keys'}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-5 py-2.5 text-sm font-bold text-white hover:brightness-110 transition"
              >
                {isAuthenticated ? 'Your API keys →' : 'Get your API key →'}
              </Link>
            </div>
          </div>
        </section>

        {/* Two-panel layout */}
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="flex gap-10 py-10">

            {/* ── Sticky sidebar ─────────────────────────────────────────── */}
            <aside className="hidden lg:block w-48 shrink-0">
              <nav className="sticky top-28">
                {NAV.map((item, i) =>
                  item.group ? (
                    <p key={i} className={`${i > 0 ? 'mt-6' : ''} mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 px-2`}>
                      {item.group}
                    </p>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={`relative flex w-full items-center gap-2 px-2 py-1.5 text-left text-[13px] transition rounded-md ${
                        activeSection === item.id
                          ? 'text-primary font-semibold'
                          : 'text-slate-500 hover:text-slate-200'
                      }`}
                    >
                      {activeSection === item.id && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full bg-primary" />
                      )}
                      {item.method && <MethodBadge method={item.method} />}
                      <span className={`truncate ${item.method ? 'font-mono text-[11px]' : ''}`}>{item.label}</span>
                    </button>
                  )
                )}
              </nav>
            </aside>

            {/* ── Main content ────────────────────────────────────────────── */}
            <div ref={mainRef} className="flex-1 min-w-0 space-y-10">

              {/* ── Quick Start ─────────────────────────────────────────── */}
              <div id="quickstart" className="scroll-mt-28">
                <SectionCard>
                  <div className="px-7 pt-7 pb-5 border-b border-white/10">
                    <SectionTitle title="Quick Start" />
                    <p className="mt-1 text-sm text-slate-400">Make your first simulation call in three steps.</p>
                  </div>
                  <div className="px-7 py-6 space-y-3">
                    {[
                      { n: '1', title: 'Sign up and get a key', body: 'Create a free account at feuteuristix.com. Head to Console → API Keys, create a new key, and copy it. Takes under 2 minutes.' },
                      { n: '2', title: 'POST your cargo payload', body: 'Send your items and container specs to api.feuteuristix.com/simulations with your Bearer token. The engine runs in milliseconds.' },
                      { n: '3', title: 'Parse the result', body: 'The response gives you packedContainers with stats, per-item 3D coordinates, cost analysis, and a simulation ID for the shareable viewer.' },
                    ].map((s) => (
                      <div key={s.n} className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-xs font-black text-primary">{s.n}</span>
                        <div>
                          <p className="text-sm font-bold text-white">{s.title}</p>
                          <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{s.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-7 pb-7">
                    <SubLabel>Minimal request shape</SubLabel>
                    <CodeBlock
                      language="http"
                      code={`POST https://api.feuteuristix.com/simulations\nx-api-key: ftx_live_••••••••••••••••••••••••\nContent-Type: application/json\n\n{\n  "handlingType": "hand_stuffed",\n  "measurementUnits": { "distance": "meter", "weight": "kilogram", "currency": "usd", "utilisation": "percent" },\n  "items":      { "Carton-A": { ... } },\n  "containers": { "20ft Standard": { ... } }\n}`}
                    />
                  </div>
                </SectionCard>
              </div>

              {/* ── Authentication ─────────────────────────────────────────── */}
              <div id="authentication" className="scroll-mt-28">
                <SectionCard>
                  <div className="px-7 pt-7 pb-5 border-b border-white/10">
                    <SectionTitle title="Authentication" />
                    <p className="mt-1 text-sm text-slate-400">
                      All requests require a Bearer token in the <code className="rounded bg-white/10 px-1 font-mono text-xs text-slate-200">Authorization</code> header.
                    </p>
                  </div>
                  <div className="px-7 py-6 space-y-5">
                    <CodeBlock language="http" code={`x-api-key: ftx_live_<your_api_key>`} />
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {[
                        { icon: 'lock',          title: 'Hashed storage',   body: 'Only a SHA-256 hash is stored. Lose a key; revoke it and create a new one.' },
                        { icon: 'visibility_off', title: 'View once',        body: 'Shown exactly once at creation. Store it in your secrets manager immediately.' },
                        { icon: 'warning',        title: 'Never expose',     body: 'Never commit to source control or expose in client-side code.' },
                        { icon: 'delete',         title: 'Revoke anytime',   body: 'Console → API Keys → Revoke. Takes effect instantly.' },
                      ].map((f) => (
                        <div key={f.title} className="flex gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
                          <span className="material-symbols-outlined mt-0.5 shrink-0 text-[16px] text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>{f.icon}</span>
                          <div>
                            <p className="text-sm font-bold text-white">{f.title}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{f.body}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* ── Rate Limits ───────────────────────────────────────────── */}
              <div id="rate-limits" className="scroll-mt-28">
                <SectionCard>
                  <div className="px-7 pt-7 pb-5 border-b border-white/10">
                    <SectionTitle title="Rate Limits" />
                    <p className="mt-1 text-sm text-slate-400">
                      Applied per API key. Exceeding returns <code className="rounded bg-white/10 px-1 font-mono text-xs">429 Too Many Requests</code>.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 bg-white/[0.03]">
                          {['Plan', 'Simulations / month', 'Requests / min', 'Max items', 'Max containers'].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="text-slate-300">
                        {[
                          ['Free',       '10',        '10',  '20', '5'],
                          ['Growth',     '1,000',     '60',  '50', '10'],
                          ['Enterprise', 'Unlimited', '300', '∞',  '∞'],
                        ].map(([plan, ...rest]) => (
                          <tr key={plan} className="border-b border-white/5 last:border-0">
                            <td className="px-5 py-3.5 font-semibold text-white">{plan}</td>
                            {rest.map((v, i) => <td key={i} className="px-5 py-3.5 text-sm">{v}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="px-7 py-4 text-xs text-slate-500 border-t border-white/10">
                    Need higher limits?{' '}
                    <Link to="/contact-sales" className="text-primary hover:underline">
                      Talk to us about enterprise.
                    </Link>
                  </p>
                </SectionCard>
              </div>

              {/* ── API Reference divider ────────────────────────────────── */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 px-1">API Reference</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* ── POST /simulations ─────────────────────────────────────── */}
              <div id="create-simulation" className="scroll-mt-28">
                <SectionCard>
                  <EndpointBanner
                    method="POST"
                    path="/simulations"
                    summary="Runs the packing engine. Returns the cheapest container combination, full packing plan with per-item 3D coordinates, utilisation stats, and a simulation ID for the shareable 3D viewer."
                  />

                  <div className="px-7 py-6 space-y-7">
                    {/* Request headers */}
                    <div>
                      <SubLabel>Request Headers</SubLabel>
                      <div className="rounded-xl border border-white/10 bg-slate-900/60 overflow-hidden text-sm">
                        {[
                          { field: 'x-api-key',    desc: 'ftx_live_<your_api_key>' },
                          { field: 'Content-Type', desc: 'application/json' },
                        ].map((h) => (
                          <div key={h.field} className="flex items-center gap-4 border-b border-white/5 px-4 py-2.5 last:border-0">
                            <code className="w-36 shrink-0 font-mono text-[13px] text-white">{h.field}</code>
                            <ReqBadge req="required" />
                            <code className="text-xs text-slate-400">{h.desc}</code>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Request body params */}
                    <div>
                      <SubLabel>Request Body</SubLabel>
                      <ParamTable params={CREATE_PARAMS} />
                    </div>

                    {/* Request example */}
                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <SubLabel>Example Request</SubLabel>
                        <div className="flex gap-1.5">
                          {['hand_stuffed', 'palletized'].map((t) => (
                            <button
                              key={t}
                              onClick={() => setReqTab(t)}
                              className={`rounded px-2.5 py-1 text-[11px] font-bold transition ${
                                reqTab === t ? 'bg-primary/20 text-primary' : 'text-slate-500 hover:text-white border border-white/10'
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      <JsonBlock obj={reqTab === 'hand_stuffed' ? HAND_STUFFED_REQUEST : PALLETIZED_REQUEST} label="request.json" />
                    </div>

                    {/* Response schema */}
                    <div>
                      <SubLabel>Response Schema — 201 Created</SubLabel>
                      <ParamTable params={RESPONSE_PARAMS} />
                    </div>

                    {/* Response example */}
                    <div>
                      <SubLabel>Example Response</SubLabel>
                      <JsonBlock obj={CREATE_RESPONSE} label="response.json" />
                    </div>

                    {/* Shareable viewer */}
                    <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.06] p-5">
                      <div className="flex items-start gap-3">
                        <span className="material-symbols-outlined shrink-0 mt-0.5 text-[18px] text-cyan-400" style={{ fontVariationSettings: "'FILL' 1" }}>link</span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-white">Shareable 3D viewer — no UI work required</p>
                          <p className="mt-1 text-xs leading-relaxed text-slate-400">
                            Every response contains an <code className="rounded bg-white/10 px-1 font-mono text-cyan-300">id</code>. Append it to the console URL for a full interactive 3D packing view — shareable with anyone on your team.
                          </p>
                          <code className="mt-3 block rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2.5 font-mono text-[11px] text-cyan-300 break-all">
                            https://feuteuristix.com/console?id=<span className="text-slate-500">a3f72b1c-4d5e-4f6a-b7c8-d9e0f1a2b3c4</span>
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* ── GET /simulations/:id ──────────────────────────────────── */}
              <div id="get-simulation" className="scroll-mt-28">
                <SectionCard>
                  <EndpointBanner
                    method="GET"
                    path="/simulations/:id"
                    summary="Fetches a previously created simulation. Returns the full result — entities, cost analysis, packing plan, 3D coordinates — identical to the POST response structure."
                  />

                  <div className="px-7 py-6 space-y-7">
                    <div>
                      <SubLabel>Path Parameters</SubLabel>
                      <ParamTable params={GET_PATH_PARAMS} />
                    </div>

                    <div>
                      <SubLabel>Request Headers</SubLabel>
                      <div className="rounded-xl border border-white/10 bg-slate-900/60 overflow-hidden">
                        <div className="flex items-center gap-4 px-4 py-2.5 text-sm">
                          <code className="w-36 shrink-0 font-mono text-[13px] text-white">x-api-key</code>
                          <ReqBadge req="required" />
                          <code className="text-xs text-slate-400">ftx_live_&lt;your_api_key&gt;</code>
                        </div>
                      </div>
                    </div>

                    <div>
                      <SubLabel>Example Request</SubLabel>
                      <CodeBlock language="http" code={`GET https://api.feuteuristix.com/simulations/a3f72b1c-4d5e-4f6a-b7c8-d9e0f1a2b3c4\nx-api-key: ftx_live_••••••••••••••••••••••••`} />
                    </div>

                    <div>
                      <SubLabel>Example Response — 200 OK</SubLabel>
                      <JsonBlock obj={GET_RESPONSE} label="response.json" />
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* ── Errors ────────────────────────────────────────────────── */}
              <div id="errors" className="scroll-mt-28">
                <SectionCard>
                  <div className="px-7 pt-7 pb-5 border-b border-white/10">
                    <SectionTitle title="Error Reference" />
                    <p className="mt-1 text-sm text-slate-400">
                      All errors use the shape <code className="rounded bg-white/10 px-1 font-mono text-xs">{'{ "success": false, "message": "..." }'}</code>.
                    </p>
                  </div>

                  <div className="overflow-x-auto border-b border-white/10">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 bg-white/[0.03]">
                          {['Status', 'Meaning', 'Common causes'].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['400', 'Bad Request',          'Missing fields, invalid types, dimension conflicts, item doesn\'t fit in container or pallet.', 'text-red-400'],
                          ['401', 'Unauthorized',          'Missing or invalid Authorization header, expired token.', 'text-amber-400'],
                          ['404', 'Not Found',             'No simulation exists for the given :id (GET endpoint).', 'text-amber-400'],
                          ['429', 'Too Many Requests',     'Rate limit exceeded. See X-RateLimit-Reset header for when it resets.', 'text-orange-400'],
                          ['500', 'Internal Server Error', 'Engine error. Retry with exponential back-off; contact support if it persists.', 'text-rose-400'],
                        ].map(([code, meaning, causes, color]) => (
                          <tr key={code} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015] transition-colors">
                            <td className={`px-5 py-3.5 font-mono font-bold whitespace-nowrap ${color}`}>{code}</td>
                            <td className="px-5 py-3.5 font-semibold text-slate-300 whitespace-nowrap">{meaning}</td>
                            <td className="px-5 py-3.5 text-sm text-slate-500 leading-relaxed">{causes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="px-7 py-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {[
                      { code: '400', label: 'Validation error', obj: ERROR_RESPONSE_400, color: 'text-red-400 bg-red-500/10' },
                      { code: '404', label: 'Not found',        obj: ERROR_RESPONSE_404, color: 'text-amber-400 bg-amber-500/10' },
                      { code: '500', label: 'Server error',     obj: ERROR_RESPONSE_500, color: 'text-rose-400 bg-rose-500/10' },
                    ].map((e) => (
                      <div key={e.code}>
                        <div className="mb-2 flex items-center gap-2">
                          <span className={`rounded px-1.5 py-0.5 font-mono text-xs font-bold ${e.color}`}>{e.code}</span>
                          <span className="text-xs font-semibold text-slate-400">{e.label}</span>
                        </div>
                        <JsonBlock obj={e.obj} />
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>

              {/* ── CTA ──────────────────────────────────────────────────── */}
              <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 text-center">
                <p className="text-xl font-black text-white">Ready to integrate?</p>
                <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
                  Free tier — 10 simulations/month. Enterprise and white-label API pricing for logistics platforms.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Link to="/login?mode=signup" className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 py-2.5 text-sm font-bold text-white hover:brightness-110 transition">
                    Get API key — free
                  </Link>
                  <Link to="/contact-sales" className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-2.5 text-sm font-bold text-white hover:bg-white/10 transition">
                    Enterprise pricing
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
